// src/pages/Blog.tsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { BlogPost } from '../types';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/api';

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await apiService.getBlogs();
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Get current posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Blog - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Stay updated with the latest news, stories, and insights from Impact Generation Africa's work in sustainable development." 
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 bg-success text-white"
        style={{ marginTop: '76px' }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <motion.h1 
                className="display-5 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Our Blog
              </motion.h1>
              <motion.p 
                className="lead mb-0"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Stories, insights, and updates from our work across African communities
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-5">
        <div className="container">
          {loading ? (
            <LoadingSpinner text="Loading blog posts..." />
          ) : (
            <>
              <div className="row">
                {currentPosts.length > 0 ? (
                  currentPosts.map((post, index) => (
                    <motion.div 
                      key={post.id}
                      className="col-md-6 col-lg-4 mb-4"
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className="card h-100 shadow-sm border-0">
                        {post.image ? (
                          <img 
                            src={`http://localhost/iga/backend/uploads/${post.image}`}
                            className="card-img-top"
                            alt={post.title}
                            style={{ height: '200px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="card-img-top bg-light d-flex align-items-center justify-content-center"
                            style={{ height: '200px' }}
                          >
                            <i className="bi bi-file-text display-4 text-muted"></i>
                          </div>
                        )}
                        <div className="card-body d-flex flex-column">
                          <div className="mb-2">
                            <small className="text-muted">
                              {formatDate(post.created_at)} • By {post.author}
                            </small>
                          </div>
                          <h5 className="card-title text-success">{post.title}</h5>
                          <p className="card-text text-muted flex-grow-1">
                            {post.excerpt || post.content.substring(0, 120) + '...'}
                          </p>
                          <a href={`/blog/${post.slug}`} className="btn btn-outline-success mt-auto">
                            Read More
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <div className="text-muted">
                      <i className="bi bi-file-text display-1 d-block mb-3"></i>
                      <h4>No blog posts yet</h4>
                      <p>Check back later for updates and stories from our work.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {posts.length > postsPerPage && (
                <nav className="mt-5">
                  <ul className="pagination justify-content-center">
                    {Array.from({ length: Math.ceil(posts.length / postsPerPage) }, (_, i) => (
                      <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => paginate(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;